import globalFormStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { PatientDataRequestToast } from '@lmt/unicorn-shared/app/components/patients/notifications/PatientDataRequestToast';
import RequestPatientIcon from '@lmt/unicorn-shared/app/icons/toolbar-icons/request.svg?react';
import { useEventCallback } from '@mui/material';
import React from 'react';
import { ToolBarButton } from '../ToolBarComponents';
import { toast } from 'react-toastify';

import { HL7MessageGroup, QueryFactory } from '@lmt/shared-types';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

import dialogStyles from '@lmt/unicorn-shared/app/components/common/Dialog/Dialog.module.scss';
import { Input } from '../../common/ui-kit/inputs/Input';
import { Field } from '../../common/Field';
import { DropDownInput } from '../../common/ui-kit/inputs/dropdown/DropDownInput';
import { useSubtenantsDict } from '../../../features/subtenants/selectors';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

type RequestPatientResult = {
  number: string;
  subtenantId: number;
};
export const RequestPatientButton = () => {
  const i18n = useScopedTranslation('toolbar');
  const i18nRoot = useTranslation();
  const claims = useClaims();
  const modals = useModal();
  const subtenants = useSubtenantsDict();
  const { data: hl7TemplateRules } =
    QueryFactory.HL7TemplateRulesQuery.useGetQuery(
      {
        isActive: true,
        messageGroup: HL7MessageGroup.Query,
      },
      {
        enabled: claims.permissions.hl7,
      },
    );

  const isAnyQueryRuleActive =
    !!hl7TemplateRules && hl7TemplateRules.length > 0;
  const requestPatient = useEventCallback(async () => {
    if (!isAnyQueryRuleActive) {
      return;
    }
    const subtenantsForDropdown = hl7TemplateRules
      .map((x) => subtenants[x.subtenantId])
      .filter((x) => !!x);
    const result = await modals.showCustom<{
      number: string;
      numberError?: string;
      subtenantId: number;
      subtenantIdError?: string;
    }>({
      validate: (result) => {
        if (!result) return false;

        if (!result.number) {
          result.numberError = i18nRoot.t('errors.required');
        }
        if (!result.subtenantId) {
          result.subtenantIdError = i18nRoot.t('errors.required');
        }
        return !!result.number && !!result.subtenantId;
      },
      defaultValue: {
        number: '',
        subtenantId:
          hl7TemplateRules.length === 1
            ? hl7TemplateRules[0].subtenantId
            : null!,
      },
      Component: (props) => {
        return (
          <div>
            <Field
              title={i18n.t('request_patient_text')}
              className={globalFormStyles.field}
              required={true}
            >
              <Input
                autoFocus={true}
                value={props.value?.number}
                onChange={(e) =>
                  props.setValue({ ...props.value!, number: e.target.value })
                }
                errorText={props.value?.numberError}
              />
            </Field>
            {hl7TemplateRules.length > 1 && (
              <Field
                title={i18n.t('request_subtenant_title')}
                className={globalFormStyles.field}
                required={true}
              >
                <DropDownInput
                  required={true}
                  useIdFunctionAsValue={true}
                  value={props.value?.subtenantId ?? null}
                  options={subtenantsForDropdown}
                  idFunction={'id'}
                  getOptionLabel={'code'}
                  variant={'formInput'}
                  onValueChanged={(o) =>
                    props.setValue({ ...props.value!, subtenantId: o.id })
                  }
                  errorText={props.value?.subtenantIdError}
                />
              </Field>
            )}
          </div>
        );
      },
      title: i18n.t('request_patient_title'),
      okButtonText: i18n.t('request_patient_button'),
    });
    if (!result) return;

    try {
      await QueryFactory.HL7OutgoingMessageClient.sendPatientRequestMessage({
        patientNumber: result.number,
        subtenantId: result.subtenantId,
      });
      toast(<PatientDataRequestToast />, {
        autoClose: false,
      });
    } catch (error) {
      await modals.showError({ text: convertToErrorString(error) });
    }
  });
  return (
    <>
      {isAnyQueryRuleActive && claims.permissions.hl7 && (
        <ToolBarButton
          icon={RequestPatientIcon}
          title={i18n.t('request_patient')}
          onClick={requestPatient}
          testId="toolbar-requestPatient"
        />
      )}
    </>
  );
};
